import { useCallback, useEffect, useMemo, useState } from 'react';
import { getPaymentSourceCardInfo } from '../../utils/getPaymentSourceCardInfo/getPaymentSourceCardInfo';
import type { BookingPaymentSource } from '../../types/api/v2/obt/model/booking-payment-source';
import { PaymentSourceType } from '../../types/api/v2/obt/model/payment-source-type';

type TabLabel<T> = (label: string) => T;

interface UsePaymentSourceTypesAndTabs<T> {
  selectedPaymentSourceIds: string[];
  paymentSources: BookingPaymentSource[];
  tabLabel: TabLabel<T>;
  onPaymentSourceSelect: (selectedPaymentSources: BookingPaymentSource[]) => void;
  allowAddCard: boolean;
  selectAdditionalPaymentSource: boolean;
}

const tabsOrder = ['CUSTOM_PAYMENT_METHOD', 'CARD', 'VIRTUAL_CARD', 'VENDOR_PROGRAM_PAYMENT'];

export const usePaymentSourceTypesAndTabs = <T>({
  paymentSources,
  tabLabel,
  selectedPaymentSourceIds = [],
  onPaymentSourceSelect,
  allowAddCard,
  selectAdditionalPaymentSource,
}: UsePaymentSourceTypesAndTabs<T>) => {
  const [selectedPaymentSourceType, setSelectedPaymentSourceType] = useState<PaymentSourceType | null>(null);

  type Tabs = Record<string, T>;
  const allTabs: Tabs = useMemo(
    () => ({
      CUSTOM_PAYMENT_METHOD: tabLabel('Brex budget'),
      CARD: tabLabel('Credit cards'),
      VIRTUAL_CARD: tabLabel('Virtual cards'),
      VENDOR_PROGRAM_PAYMENT: tabLabel('Direct bill'),
    }),
    [tabLabel],
  );

  const modifiedPaymentSources = paymentSources.map((paymentSource) => {
    if (
      paymentSource.type === PaymentSourceType.VendorProgramPayment &&
      paymentSource?.metadata &&
      'vendorProgramPaymentDescriptor' in paymentSource?.metadata &&
      paymentSource.metadata.vendorProgramPaymentDescriptor?.vendorProgramPaymentMetadata &&
      'airlineProgram' in paymentSource.metadata.vendorProgramPaymentDescriptor?.vendorProgramPaymentMetadata
    ) {
      return {
        ...paymentSource,
        type: PaymentSourceType.Card,
      };
    }
    return paymentSource;
  });

  // Check if the selected payment source is still eligible to be selected.
  const modifiedPaymentSourceIds = modifiedPaymentSources.map((paymentSource) => paymentSource.id);
  const eligibleSelectedPaymentSourceIds = selectedPaymentSourceIds.filter((paymentSourceId) =>
    modifiedPaymentSourceIds.includes(paymentSourceId),
  );

  const paymentSourceTypes = useMemo(() => {
    const sourceTypes = Array.from(new Set(modifiedPaymentSources.map((paymentSource) => paymentSource.type)));
    sourceTypes.sort((a, b) => tabsOrder.indexOf(a) - tabsOrder.indexOf(b));

    // added this so that brex budget is selected as default if brex budget and brex rewards both present
    const isBrexRewardsPresentInSourceTypes = sourceTypes.find((st) => st === PaymentSourceType.RewardsProgram);
    const isBrexBudgetPresentInSourceTypes = sourceTypes.find((st) => st === PaymentSourceType.CustomPaymentMethod);
    if (isBrexRewardsPresentInSourceTypes && isBrexBudgetPresentInSourceTypes) {
      const index = sourceTypes.findIndex((st) => st === PaymentSourceType.RewardsProgram);
      sourceTypes.splice(index, 1);
      sourceTypes.push(PaymentSourceType.RewardsProgram);
    }

    return sourceTypes;
  }, [modifiedPaymentSources]);

  const paymentSourcesRequired = modifiedPaymentSources.filter((paymentSource) => paymentSource.isRequired);

  const tabs = useMemo(() => {
    const tabsList: Tabs = {};
    const paymentSourceWithAirlineProgram = modifiedPaymentSources.find(
      (paymentSource) =>
        paymentSource?.metadata &&
        'vendorProgramPaymentDescriptor' in paymentSource?.metadata &&
        paymentSource.metadata.vendorProgramPaymentDescriptor?.vendorProgramPaymentMetadata &&
        'airlineProgram' in paymentSource.metadata.vendorProgramPaymentDescriptor?.vendorProgramPaymentMetadata,
    );

    paymentSourceTypes.forEach((paymentSourceType) => {
      if (
        !(paymentSourceWithAirlineProgram && paymentSourceType === PaymentSourceType.VendorProgramPayment) &&
        allTabs[paymentSourceType.toString()]
      ) {
        tabsList[paymentSourceType.toString()] = allTabs[paymentSourceType.toString()];
      }
    });
    if (allowAddCard) {
      tabsList[PaymentSourceType.Card.toString()] = allTabs[PaymentSourceType.Card.toString()];
    }
    return tabsList;
  }, [allTabs, allowAddCard, paymentSourceTypes, modifiedPaymentSources]);

  const handlePaymentSourceSelect = useCallback(
    (paymentSource?: BookingPaymentSource) => {
      const selectedPaymentSources = [];
      if (paymentSource) {
        setSelectedPaymentSourceType(paymentSource.type);
        selectedPaymentSources.push(paymentSource);
      }
      if (
        selectAdditionalPaymentSource &&
        paymentSourcesRequired.length !== modifiedPaymentSources.length &&
        paymentSourcesRequired.find((source) => source.isUsable)?.isUsable
      ) {
        // to handle United UAPass plus case and should not be selected when other payment sources are not present
        selectedPaymentSources.push(paymentSourcesRequired[0]);
      }
      if (selectedPaymentSources.length > 0 && selectedPaymentSources.find((source) => source.isUsable)?.isUsable) {
        onPaymentSourceSelect(selectedPaymentSources);
      }
    },
    [modifiedPaymentSources, onPaymentSourceSelect, paymentSourcesRequired, selectAdditionalPaymentSource],
  );

  useEffect(() => {
    if (
      (selectedPaymentSourceIds.length === 0 || eligibleSelectedPaymentSourceIds.length === 0) &&
      modifiedPaymentSources.length > 0
    ) {
      // Set the default payment source when the user lands on the page or when the selectedPaymentSourceId is
      // no longer eligible as a selected payment source.
      const paymentSource = modifiedPaymentSources.find(
        (paymentSourceItem) =>
          paymentSourceItem.type === paymentSourceTypes[0] &&
          !paymentSourceItem.isRequired &&
          !getPaymentSourceCardInfo(paymentSourceItem).isCardExpired,
      );
      handlePaymentSourceSelect(paymentSource);
    } else if (!selectedPaymentSourceType && selectedPaymentSourceIds.length > 0 && modifiedPaymentSources.length > 0) {
      // to set the payment source type when the selectedPaymentSourceId is present
      const paymentSource = modifiedPaymentSources.find(
        (paymentSourceItem) =>
          paymentSourceItem.id === selectedPaymentSourceIds[0] &&
          !paymentSourceItem.isRequired &&
          !getPaymentSourceCardInfo(paymentSourceItem).isCardExpired,
      );
      handlePaymentSourceSelect(paymentSource);
    } else if (modifiedPaymentSources.length === 0 && allowAddCard) {
      setSelectedPaymentSourceType(PaymentSourceType.Card);
    }
  }, [
    allowAddCard,
    handlePaymentSourceSelect,
    paymentSourceTypes,
    modifiedPaymentSources,
    selectedPaymentSourceIds,
    selectedPaymentSourceType,
    eligibleSelectedPaymentSourceIds,
  ]);

  const handlePaymentSourceTypeSelect = (paymentSourceType: PaymentSourceType) => {
    setSelectedPaymentSourceType(paymentSourceType);
    const paymentSource = modifiedPaymentSources.find((source) => source.type === paymentSourceType && source.isUsable);
    handlePaymentSourceSelect(paymentSource);
  };

  const paymentSourcesWithSelectedType = modifiedPaymentSources.filter(
    (paymentSource) => paymentSource.type === selectedPaymentSourceType && !paymentSource.isRequired,
  );

  return {
    tabs,
    selectedPaymentSourceType: selectedPaymentSourceType || paymentSourceTypes[0],
    paymentSourcesWithSelectedType,
    paymentSourceTypes,
    handlePaymentSourceTypeSelect,
    paymentSourcesRequired,
  };
};
